import { ChangeDetectionStrategy, Component, computed, Input, signal } from "@angular/core";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { EventService } from "../../../services/event.service";
import { Role } from "../../s25-event/EventMicroI";
import { EventMicroService } from "../../../services/event.micro.service";

export type ContactRole = {
    itemId: number;
    contact: S25ItemI;
    itemLabel: string;
};

@TypeManagerDecorator("s25-ng-editable-contact-roles")
@Component({
    selector: "s25-ng-editable-contact-roles",
    template: `
        @for (role of contactsSignal(); track role.itemId; let last = $last) {
            <div class="roles-container" [class.last-child]="last">
                <label>{{ role.itemLabel }}</label>
                <div class="contact-wrapper">
                    @if (editable) {
                        <s25-ng-dropdown-search-criteria
                            [type]="'contacts'"
                            [(chosen)]="role.contact"
                            (chosenChange)="updateRoles(role)"
                        ></s25-ng-dropdown-search-criteria>
                        @if (+role.itemId > 0) {
                            <button
                                (click)="updateRoles(role, true)"
                                class="aw-button aw-button--danger--transparent"
                                [class.show-remove]="showAddContact()"
                            >
                                Remove
                            </button>
                        }
                    } @else {
                        <s25-item-contact
                            [modelBean]="{ itemId: +role.contact.itemId, itemName: role.contact.itemName }"
                            [includeTypeIcon]="true"
                            class="editableFalse"
                        ></s25-item-contact>
                    }
                </div>
            </div>
        }
        <div class="edit-wrapper c-margin-top--quarter" [hidden]="!editable">
            <button (click)="showAddContact.set(!showAddContact())" class="aw-button aw-button--primary">
                Edit Contacts
            </button>
            @if (showAddContact()) {
                <select [(ngModel)]="roleToAdd" (change)="addRole()" class="cn-form__control">
                    <option [ngValue]="null">
                        {{ availableRoles().length ? "Select a Contact Role" : "All Contact Roles Assigned" }}
                    </option>
                    @for (role of availableRoles(); track role) {
                        <option [ngValue]="role" class="ngListPageDropdown">
                            {{ role.roleName }}
                        </option>
                    }
                </select>
            }
        </div>
    `,
    host: {
        "[class.read-only]": "!editable",
    },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 5px;

            .roles-container {
                display: grid;
                grid-template-columns: 30% 70%;
                align-items: center;

                label {
                    grid-column: 1 / 2;
                }

                .contact-wrapper {
                    grid-column: 2 / 3;
                    display: flex;

                    s25-ng-dropdown-search-criteria {
                        flex: 1 1 auto;
                        transition: flex-basis 0.5s ease-in;
                    }

                    button {
                        flex: 0 0 0;
                        padding: 0;
                        opacity: 0;
                        overflow: hidden;
                        transition:
                            opacity 1s ease-in-out,
                            flex-basis 0.5s ease-out;

                        &.show-remove {
                            flex-basis: 80px;
                            opacity: 1;
                            margin-left: 5px;
                        }
                    }
                }
            }

            .edit-wrapper {
                margin-left: 30%;
                display: flex;
                gap: 1em;
            }

            @media screen and (max-width: 500px) {
                .edit-wrapper {
                    flex-direction: column-reverse;
                    margin-inline: 10px;
                    gap: 0.625rem;

                    button,
                    select {
                        width: 100%;
                    }
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditableContactRolesComponent {
    @Input() itemId: number;
    @Input() set contacts(value: ContactRole[]) {
        this.contactsSignal.set(value ?? []);
    }
    @Input() set eventTypeRoles(value: Role[]) {
        this.eventTypesSignal.set(value ?? []);
    }
    @Input() editable: boolean;

    contactsSignal = signal<ContactRole[]>([]);
    eventTypesSignal = signal<Role[]>([]);
    showAddContact = signal(false);
    availableRoles = computed(() =>
        this.eventTypesSignal().filter(
            (role) => !this.contactsSignal().some((contact) => contact.itemId === role.roleId),
        ),
    );
    roleToAdd: Role = null;

    async updateRoles(role: ContactRole, isDelete?: boolean) {
        if (isDelete)
            this.contactsSignal.update((contacts) => [
                ...contacts.filter((contactRole) => contactRole.itemId !== role.itemId),
            ]);
        this.showAddContact.set(false);
        if (role.contact?.itemId) {
            let payload: any = { roles: [{ roleId: +role.itemId, contactId: +role.contact.itemId }] };
            if (isDelete) payload = { remove: payload };
            const [_, err] = await S25Util.Maybe(
                EventMicroService.microPutEventDetail({ items: [payload] }, this.itemId),
            );
            if (err) return console.error(err);
        }
    }

    addRole() {
        this.contactsSignal.update((contacts) => [
            ...contacts,
            { itemId: this.roleToAdd.roleId, itemLabel: this.roleToAdd.roleName, contact: null },
        ]);
        this.showAddContact.set(false);
    }
}
